import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList as TabsListBase, TabsTrigger as TabsTriggerBase, TabsContent as TabsContentBase } from '@/components/ui/tabs';
const TabsContent = TabsContentBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode }>;
const TabsTrigger = TabsTriggerBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode; 'data-testid'?: string }>;
const TabsList = TabsListBase as unknown as React.FC<{ className?: string; children?: React.ReactNode }>;
import { toast } from 'sonner';
import { Save, History, AlertTriangle, Info, Plus, Trash2, ShieldCheck, ChevronLeft, Link2, Pencil, RefreshCw } from 'lucide-react';
import { JbMatrixEditor } from '@/pages/settings/JbMatrixEditor';

interface Factor {
  factor_id: string;
  name: string;
  cluster: 'A' | 'B' | 'C';
  max_points: number;
  question_code: string;
  scoring: string;
  worst_answer?: string;
}
interface Band { band: string; upper_exclusive: number | null; label: string; }
interface Multiplier { lower_bound: number; multiplier: number; }
interface Bonus { lower_bound: number; bonus: number; }
interface CashBand { lower_bound: number; points: number; }

interface RatingConfig {
  factors: Factor[];
  bad_answer_threshold: number;
  category_weights: Record<string, number>;
  cluster_multipliers: Multiplier[];
  cross_category_bonus: Bonus[];
  high_risk_category_threshold: number;
  power_backup_points_by_count: Record<string, number>;
  cash_value_bands: CashBand[];
  psira_grade_points: Record<string, number>;
  crime_rating_points: Record<string, number>;
  floor_type_points: Record<string, number>;
}

interface MatrixDetail {
  id: string;
  name: string;
  description: string;
  engine: string;
  version_number: number;
  current_version_id: string;
  bands: Band[];
  config: RatingConfig;
  linked_templates: Array<{ id: string; name: string }>;
}

interface VersionRow {
  id: string;
  version_number: number;
  engine: string;
  is_current: boolean;
  change_summary?: string;
  created_at: string;
  created_by_email: string | null;
}

const EMPTY_CONFIG: RatingConfig = {
  factors: [],
  bad_answer_threshold: 0.35,
  category_weights: { security: 0.55, power_backup: 0.10, cash_value: 0.35 },
  cluster_multipliers: [],
  cross_category_bonus: [],
  high_risk_category_threshold: 50,
  power_backup_points_by_count: {},
  cash_value_bands: [],
  psira_grade_points: {},
  crime_rating_points: {},
  floor_type_points: {},
};

export const RatingTablesPage: React.FC = () => {
  const { matrixId } = useParams<{ matrixId?: string }>();
  const nav = useNavigate();
  const [matrix, setMatrix] = useState<MatrixDetail | null>(null);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');

  const load = async () => {
    if (!matrixId) return;
    try {
      const [r, v] = await Promise.all([
        api.get<MatrixDetail>(`/risk/matrices/${matrixId}`),
        api.get<VersionRow[]>(`/risk/matrices/${matrixId}/versions`),
      ]);
      const config: RatingConfig = { ...EMPTY_CONFIG };
      Object.assign(config, r.data.config || {});
      setMatrix({ ...r.data, config, bands: r.data.bands || [] });
      setVersions(v.data);
      setDirty(false);
      setChangeSummary('');
    } catch (err) {
      console.warn('matrix load failed', err);
      toast.error('Failed to load matrix — it may have been archived.');
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [matrixId]);

  const weightSum = useMemo(() => {
    return Object.values(matrix?.config?.category_weights || {}).reduce((s, v) => s + Number(v || 0), 0);
  }, [matrix?.config?.category_weights]);

  if (!matrixId) {
    return <div className="text-raptor-navy/60 text-center py-10">No matrix selected. <button className="underline" onClick={() => nav('..')}>Back to list</button></div>;
  }
  if (!matrix) return <div className="text-raptor-navy/50 text-center py-10" data-testid="matrix-loading">Loading matrix…</div>;

  if (matrix.engine === 'jb_v1') {
    return <JbMatrixEditor matrix={matrix as unknown as React.ComponentProps<typeof JbMatrixEditor>['matrix']} versions={versions} onReload={load} />;
  }

  const publish = async () => {
    if (!changeSummary.trim()) {
      toast.error('Please describe what changed — this is stamped on the version history.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/risk/matrices/${matrixId}/versions`, {
        engine: matrix.engine,
        bands: matrix.bands,
        config: matrix.config,
        change_summary: changeSummary,
      });
      toast.success(`New version published — existing assessments remain on v${matrix.version_number}`);
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Publish failed';
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const patchConfig = (patch: Partial<RatingConfig>) => {
    setMatrix((t) => t ? { ...t, config: { ...t.config, ...patch } } : t);
    setDirty(true);
  };
  const patchBands = (bands: Band[]) => {
    setMatrix((t) => t ? { ...t, bands } : t);
    setDirty(true);
  };

  const weightSumOk = Math.abs(weightSum - 1) < 0.001;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="rating-tables-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-raptor-navy/60 text-xs">
            <button className="hover:underline flex items-center gap-1" onClick={() => nav('..')} data-testid="back-to-matrices">
              <ChevronLeft size={12} /> Risk matrices
            </button>
          </div>
          <h2 className="font-headline text-2xl text-raptor-navy tracking-wide mt-1 truncate">{matrix.name}</h2>
          <p className="text-sm text-raptor-navy/60 font-body max-w-2xl">
            {matrix.description || 'Rating rules for this matrix. Editing does'} <b>not</b> re-score existing assessments — changes apply to new assessments, renewals, and addenda only.
          </p>
          {matrix.linked_templates.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-raptor-navy/60 font-body">
              <Link2 size={12} /> Linked templates:
              {matrix.linked_templates.map((t) => (
                <Badge key={t.id} variant="muted" className="text-[10px]">{t.name}</Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="blue">v{matrix.version_number} · {matrix.engine}</Badge>
          {dirty && <Badge variant="warning"><AlertTriangle size={10} /> Unsaved changes</Badge>}
        </div>
      </div>

      {!weightSumOk && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="weight-warning">
          <AlertTriangle size={16} /> Category weights sum to {weightSum.toFixed(2)} — must equal 1.00 before you can publish.
        </div>
      )}

      <Tabs defaultValue="factors">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="factors" data-testid="tab-factors">Factors</TabsTrigger>
          <TabsTrigger value="point-tables" data-testid="tab-point-tables">Point tables</TabsTrigger>
          <TabsTrigger value="multipliers" data-testid="tab-multipliers">Multipliers &amp; bonus</TabsTrigger>
          <TabsTrigger value="weights" data-testid="tab-weights">Weights &amp; threshold</TabsTrigger>
          <TabsTrigger value="bands" data-testid="tab-bands">Bands</TabsTrigger>
          <TabsTrigger value="abm-library" data-testid="tab-abm-library">ABM Library</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Version history</TabsTrigger>
        </TabsList>

        {/* -------- Factors -------- */}
        <TabsContent value="factors" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Risk factors ({matrix.config.factors.length})</CardTitle>
              <CardDescription>Each factor sits in a cluster and awards up to <i>Max points</i> when the answer is the <i>Worst answer</i>. Under 35% of max = &ldquo;good&rdquo;, over 35% = &ldquo;bad&rdquo; and counts toward cluster compounding.</CardDescription>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
                  <tr>
                    <th className="py-2 px-3 text-left">Factor</th>
                    <th className="py-2 px-3 text-left">Question code</th>
                    <th className="py-2 px-3 text-left">Cluster</th>
                    <th className="py-2 px-3 text-right">Max pts</th>
                    <th className="py-2 px-3 text-left">Scoring kind</th>
                    <th className="py-2 px-3 text-left">Worst answer</th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.config.factors.map((f, i) => (
                    <tr key={`factor-${i}-${f.name}`} className="border-t border-border/60">
                      <td className="py-2 px-3">
                        <Input value={f.name} className="h-7 text-xs" onChange={(e) => {
                          const factors = [...matrix.config.factors]; factors[i] = { ...f, name: e.target.value }; patchConfig({ factors });
                        }} />
                      </td>
                      <td className="py-2 px-3">
                        <Input value={f.question_code} className="h-7 text-xs font-tabular" onChange={(e) => {
                          const factors = [...matrix.config.factors]; factors[i] = { ...f, question_code: e.target.value }; patchConfig({ factors });
                        }} />
                      </td>
                      <td className="py-2 px-3">
                        <Select value={f.cluster} className="h-7 text-xs w-32" onChange={(e) => {
                          const factors = [...matrix.config.factors]; factors[i] = { ...f, cluster: e.target.value as 'A' | 'B' | 'C' }; patchConfig({ factors });
                        }}>
                          <option value="A">A</option><option value="B">B</option><option value="C">C</option>
                        </Select>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <Input type="number" step="0.5" min="0" value={f.max_points} className="h-7 text-xs text-right w-16 font-tabular ml-auto" onChange={(e) => {
                          const factors = [...matrix.config.factors]; factors[i] = { ...f, max_points: Number(e.target.value) }; patchConfig({ factors });
                        }} />
                      </td>
                      <td className="py-2 px-3">
                        <Select value={f.scoring} className="h-7 text-xs w-44" onChange={(e) => {
                          const factors = [...matrix.config.factors]; factors[i] = { ...f, scoring: e.target.value }; patchConfig({ factors });
                        }}>
                          <option value="yes_no_worst">Yes/No (worst answer)</option>
                          <option value="psira_grade">PSIRA grade</option>
                          <option value="crime_rating">Crime rating</option>
                          <option value="floor_type">Floor type</option>
                          <option value="date_freshness_6m">Date freshness (6mo)</option>
                        </Select>
                      </td>
                      <td className="py-2 px-3">
                        {f.scoring === 'yes_no_worst' ? (
                          <Select value={f.worst_answer || 'No'} className="h-7 text-xs w-20" onChange={(e) => {
                            const factors = [...matrix.config.factors]; factors[i] = { ...f, worst_answer: e.target.value }; patchConfig({ factors });
                          }}><option value="No">No</option><option value="Yes">Yes</option></Select>
                        ) : <span className="text-xs text-raptor-navy/40">n/a</span>}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => {
                          const factors = matrix.config.factors.filter((_, j) => j !== i); patchConfig({ factors });
                        }} data-testid={`delete-factor-${i}`}><Trash2 size={12} /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-3">
                <Button variant="outline" size="sm" onClick={() => {
                  const newFactor: Factor = {
                    factor_id: `f_${Date.now()}`, name: 'New factor', cluster: 'A',
                    max_points: 1, question_code: '', scoring: 'yes_no_worst', worst_answer: 'No',
                  };
                  patchConfig({ factors: [...matrix.config.factors, newFactor] });
                }} data-testid="add-factor-btn"><Plus size={12} /> Add factor</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Point Tables -------- */}
        <TabsContent value="point-tables" className="mt-4 space-y-3">
          <PointTableCard title="PSIRA Company Grading points" hint="Award for each grade. Higher points = higher risk." table={matrix.config.psira_grade_points} onChange={(t) => patchConfig({ psira_grade_points: t })} />
          <PointTableCard title="Crime Rating points" hint="Points added when the site falls in each area rating." table={matrix.config.crime_rating_points} onChange={(t) => patchConfig({ crime_rating_points: t })} />
          <PointTableCard title="Floor Type points" hint="Points awarded per floor surface (&ldquo;Other&rdquo; catches anything unmapped)." table={matrix.config.floor_type_points} onChange={(t) => patchConfig({ floor_type_points: t })} />
          <PointTableCard title="Power Backup points (by number of sources present)" hint="Key: none / one / two_or_more. Fewer sources = more risk." table={matrix.config.power_backup_points_by_count} onChange={(t) => patchConfig({ power_backup_points_by_count: t })} />
          <Card>
            <CardHeader><CardTitle className="text-base">Cash Value at Risk bands</CardTitle><CardDescription>Lower-bound R value → points awarded. Highest matching band applies.</CardDescription></CardHeader>
            <CardContent>
              <table className="w-full text-sm font-body"><thead className="text-xs text-raptor-navy/60"><tr><th className="py-1 text-left">Lower bound (R)</th><th className="py-1 text-right">Points</th><th></th></tr></thead>
                <tbody>{matrix.config.cash_value_bands.map((b, i) => (
                  <tr key={`cvb-${i}-${b.lower_bound}`} className="border-t border-border/60">
                    <td className="py-1"><Input type="number" value={b.lower_bound} className="h-7 text-xs font-tabular" onChange={(e) => {
                      const bands = [...matrix.config.cash_value_bands]; bands[i] = { ...b, lower_bound: Number(e.target.value) }; patchConfig({ cash_value_bands: bands });
                    }} /></td>
                    <td className="py-1"><Input type="number" step="0.5" value={b.points} className="h-7 text-xs font-tabular text-right" onChange={(e) => {
                      const bands = [...matrix.config.cash_value_bands]; bands[i] = { ...b, points: Number(e.target.value) }; patchConfig({ cash_value_bands: bands });
                    }} /></td>
                    <td className="py-1 text-right">
                      <Button variant="ghost" size="sm" onClick={() => patchConfig({ cash_value_bands: matrix.config.cash_value_bands.filter((_, j) => j !== i) })}><Trash2 size={12} /></Button>
                    </td>
                  </tr>
                ))}</tbody></table>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => patchConfig({ cash_value_bands: [...matrix.config.cash_value_bands, { lower_bound: 0, points: 0 }] })}><Plus size={12} /> Add band</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Multipliers -------- */}
        <TabsContent value="multipliers" className="mt-4 space-y-3">
          <Card>
            <CardHeader><CardTitle className="text-base">Cluster compounding multipliers</CardTitle><CardDescription>When a cluster has <b>N or more</b> &ldquo;bad answers&rdquo;, multiply that cluster&rsquo;s raw points by the multiplier. Highest matching rule wins.</CardDescription></CardHeader>
            <CardContent>
              <table className="w-full text-sm font-body"><thead className="text-xs text-raptor-navy/60"><tr><th className="py-1 text-left">Lower bound (bad answers ≥)</th><th className="py-1 text-right">Multiplier</th><th></th></tr></thead>
                <tbody>{matrix.config.cluster_multipliers.map((m, i) => (
                  <tr key={`cm-${i}-${m.lower_bound}`} className="border-t border-border/60">
                    <td className="py-1"><Input type="number" min="0" step="1" value={m.lower_bound} className="h-7 text-xs font-tabular" onChange={(e) => {
                      const mults = [...matrix.config.cluster_multipliers]; mults[i] = { ...m, lower_bound: Number(e.target.value) }; patchConfig({ cluster_multipliers: mults });
                    }} /></td>
                    <td className="py-1"><Input type="number" step="0.05" min="1" value={m.multiplier} className="h-7 text-xs font-tabular text-right" onChange={(e) => {
                      const mults = [...matrix.config.cluster_multipliers]; mults[i] = { ...m, multiplier: Number(e.target.value) }; patchConfig({ cluster_multipliers: mults });
                    }} /></td>
                    <td className="py-1 text-right"><Button variant="ghost" size="sm" onClick={() => patchConfig({ cluster_multipliers: matrix.config.cluster_multipliers.filter((_, j) => j !== i) })}><Trash2 size={12} /></Button></td>
                  </tr>
                ))}</tbody></table>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => patchConfig({ cluster_multipliers: [...matrix.config.cluster_multipliers, { lower_bound: 0, multiplier: 1 }] })}><Plus size={12} /> Add rule</Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Cross-category compounding bonus</CardTitle><CardDescription>Bonus points added to the weighted score when <b>N or more</b> categories score at/above the &ldquo;high-risk&rdquo; threshold. Highest matching rule wins.</CardDescription></CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>High-risk category threshold (score)</Label>
                  <Input type="number" min="0" max="100" step="1" value={matrix.config.high_risk_category_threshold} className="h-8 font-tabular" onChange={(e) => patchConfig({ high_risk_category_threshold: Number(e.target.value) })} />
                </div>
              </div>
              <table className="w-full text-sm font-body mt-3"><thead className="text-xs text-raptor-navy/60"><tr><th className="py-1 text-left">Weak categories ≥</th><th className="py-1 text-right">Bonus</th><th></th></tr></thead>
                <tbody>{matrix.config.cross_category_bonus.map((b, i) => (
                  <tr key={`ccb-${i}-${b.lower_bound}`} className="border-t border-border/60">
                    <td className="py-1"><Input type="number" min="0" step="1" value={b.lower_bound} className="h-7 text-xs font-tabular" onChange={(e) => {
                      const bonus = [...matrix.config.cross_category_bonus]; bonus[i] = { ...b, lower_bound: Number(e.target.value) }; patchConfig({ cross_category_bonus: bonus });
                    }} /></td>
                    <td className="py-1"><Input type="number" step="0.5" min="0" value={b.bonus} className="h-7 text-xs font-tabular text-right" onChange={(e) => {
                      const bonus = [...matrix.config.cross_category_bonus]; bonus[i] = { ...b, bonus: Number(e.target.value) }; patchConfig({ cross_category_bonus: bonus });
                    }} /></td>
                    <td className="py-1 text-right"><Button variant="ghost" size="sm" onClick={() => patchConfig({ cross_category_bonus: matrix.config.cross_category_bonus.filter((_, j) => j !== i) })}><Trash2 size={12} /></Button></td>
                  </tr>
                ))}</tbody></table>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => patchConfig({ cross_category_bonus: [...matrix.config.cross_category_bonus, { lower_bound: 0, bonus: 0 }] })}><Plus size={12} /> Add rule</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Weights & threshold -------- */}
        <TabsContent value="weights" className="mt-4 space-y-3">
          <Card>
            <CardHeader><CardTitle className="text-base">Category weights &amp; bad-answer threshold</CardTitle><CardDescription>Weights must sum to 1.00. &ldquo;Bad answer&rdquo; = a factor awarding more than this fraction of its max points.</CardDescription></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-3">
              {(['security', 'power_backup', 'cash_value'] as const).map((k) => (
                <div key={k}>
                  <Label>{k.replace('_', ' ')}</Label>
                  <Input type="number" step="0.01" min="0" max="1" value={matrix.config.category_weights?.[k] ?? 0} className="font-tabular" onChange={(e) => patchConfig({ category_weights: { ...matrix.config.category_weights, [k]: Number(e.target.value) } })} data-testid={`weight-${k}`} />
                </div>
              ))}
              <div>
                <Label>Bad-answer threshold (fraction of max)</Label>
                <Input type="number" step="0.01" min="0" max="1" value={matrix.config.bad_answer_threshold} className="font-tabular" onChange={(e) => patchConfig({ bad_answer_threshold: Number(e.target.value) })} />
                <p className="text-[11px] text-raptor-navy/50 mt-1">Default 0.35 — raise to require more severe misses to trigger cluster compounding.</p>
              </div>
              <div className="md:col-span-2 text-sm text-raptor-navy/70">
                Weight sum: <b className={weightSumOk ? 'text-emerald-600' : 'text-amber-600'}>{weightSum.toFixed(2)}</b>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Bands -------- */}
        <TabsContent value="bands" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Final band thresholds</CardTitle><CardDescription>A total score is classified into the first band whose <i>upper exclusive</i> is greater than the score. Leave the last band&rsquo;s upper as blank/null for &ldquo;no ceiling&rdquo;.</CardDescription></CardHeader>
            <CardContent>
              <table className="w-full text-sm font-body"><thead className="text-xs text-raptor-navy/60"><tr><th className="py-1 text-left">Band key</th><th className="py-1 text-left">Label</th><th className="py-1 text-right">Upper exclusive</th></tr></thead>
                <tbody>{matrix.bands.map((b, i) => (
                  <tr key={`band-${i}-${b.band}`} className="border-t border-border/60">
                    <td className="py-1"><Input value={b.band} className="h-7 text-xs font-tabular" onChange={(e) => { const bands = [...matrix.bands]; bands[i] = { ...b, band: e.target.value }; patchBands(bands); }} /></td>
                    <td className="py-1"><Input value={b.label} className="h-7 text-xs" onChange={(e) => { const bands = [...matrix.bands]; bands[i] = { ...b, label: e.target.value }; patchBands(bands); }} /></td>
                    <td className="py-1"><Input type="number" step="1" min="0" max="100" value={b.upper_exclusive ?? ''} placeholder="no ceiling" className="h-7 text-xs font-tabular text-right" onChange={(e) => { const bands = [...matrix.bands]; bands[i] = { ...b, upper_exclusive: e.target.value === '' ? null : Number(e.target.value) }; patchBands(bands); }} /></td>
                  </tr>
                ))}</tbody></table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- ABM Library -------- */}
        <TabsContent value="abm-library" className="mt-4">
          <ABMLibraryTab matrixId={matrixId} />
        </TabsContent>

        {/* -------- History -------- */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><History size={16} /> Version history ({versions.length})</CardTitle><CardDescription>Every publish creates a new immutable version. Historical scored records are never rewritten.</CardDescription></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm font-body"><thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70"><tr><th className="py-2 px-3 text-left">Version</th><th className="py-2 px-3 text-left">Engine</th><th className="py-2 px-3 text-left">Summary</th><th className="py-2 px-3 text-left">Author</th><th className="py-2 px-3 text-left">Created</th><th className="py-2 px-3"></th></tr></thead>
                <tbody>{versions.map((v) => (
                  <tr key={v.id} className="border-t border-border/60" data-testid={`version-row-${v.version_number}`}>
                    <td className="py-2 px-3 font-tabular font-semibold">v{v.version_number}</td>
                    <td className="py-2 px-3 text-xs text-raptor-navy/70">{v.engine}</td>
                    <td className="py-2 px-3 text-xs">{v.change_summary || <span className="text-raptor-navy/40 italic">no summary</span>}</td>
                    <td className="py-2 px-3 text-xs text-raptor-navy/60">{v.created_by_email || '—'}</td>
                    <td className="py-2 px-3 text-xs text-raptor-navy/60">{v.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="py-2 px-3">{v.is_current && <Badge variant="success"><ShieldCheck size={10} /> Current</Badge>}</td>
                  </tr>
                ))}</tbody></table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Publish bar */}
      <div className="sticky bottom-0 -mx-6 -mb-6 border-t border-border/60 bg-white/95 backdrop-blur px-6 py-3 flex items-center gap-3 flex-wrap" data-testid="publish-bar">
        <Info size={14} className="text-raptor-navy/60" />
        <div className="text-xs text-raptor-navy/70 flex-1 min-w-[200px]">
          Publishing creates <b>v{matrix.version_number + 1}</b>. Existing assessments stay on their frozen version; new / renewed / addended covers use the latest.
        </div>
        <Input placeholder="What changed? (e.g. 'Reduced fire max to 3pt')" value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} className="h-8 text-xs w-72" data-testid="change-summary" />
        <Button variant="gradient" onClick={() => void publish()} disabled={!dirty || saving || !weightSumOk} data-testid="publish-rating-btn">
          <Save size={14} className="mr-1" /> {saving ? 'Publishing…' : `Publish v${matrix.version_number + 1}`}
        </Button>
      </div>
    </div>
  );
};

const PointTableCard: React.FC<{ title: string; hint?: string; table: Record<string, number>; onChange: (t: Record<string, number>) => void }> = ({ title, hint, table, onChange }) => {
  const [newKey, setNewKey] = useState('');
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle>{hint && <CardDescription>{hint}</CardDescription>}</CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
          {Object.entries(table).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <div className="w-40 text-xs text-raptor-navy/70 font-tabular truncate" title={k}>{k}</div>
              <Input type="number" step="0.25" value={v} className="h-7 text-xs font-tabular w-24" onChange={(e) => onChange({ ...table, [k]: Number(e.target.value) })} />
              <Button variant="ghost" size="sm" onClick={() => { const t = { ...table }; delete t[k]; onChange(t); }}><Trash2 size={12} /></Button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="New key" className="h-7 text-xs w-40" />
          <Button variant="outline" size="sm" onClick={() => { if (newKey.trim()) { onChange({ ...table, [newKey.trim()]: 0 }); setNewKey(''); } }}><Plus size={12} /> Add row</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RatingTablesPage;

/* ═══════════════════════════════════════════════════════════════════════
 * ABM Library — matrix-scoped catalogue of ABM models
 * Powers the cash-exposure category of the scoring engine. Add / edit /
 * soft-delete rows here; a "Re-score" button re-runs every completed
 * assessment linked to this matrix so Analytics + KPIs update at once.
 * ═══════════════════════════════════════════════════════════════════════ */
interface ABMRow {
  id: string; old_name?: string; new_name: string; group?: string;
  type: 'notes' | 'coin'; capacity: number; purchase_price_zar?: number | null;
  cash_value_override_zar?: number | null; risk_points_override?: number | null;
  active: boolean; seeded?: boolean;
}
interface ABMBand { lower_bound: number; points: number }
interface ABMLibraryPayload {
  matrix_id: string; matrix_name: string; abm_note_value_zar: number;
  cash_value_bands: ABMBand[]; library: ABMRow[];
}

const ABMLibraryTab: React.FC<{ matrixId?: string }> = ({ matrixId }) => {
  const [data, setData] = React.useState<ABMLibraryPayload | null>(null);
  const [showInactive, setShowInactive] = React.useState(false);
  const [editing, setEditing] = React.useState<ABMRow | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [rescoring, setRescoring] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!matrixId) return;
    const r = await api.get<ABMLibraryPayload>(
      `/risk/matrices/${matrixId}/abm-library?include_inactive=${showInactive}`,
    );
    setData(r.data);
  }, [matrixId, showInactive]);
  React.useEffect(() => { void load(); }, [load]);

  /** Preview the cash value + points a row will earn given its capacity + overrides. */
  const previewFor = React.useCallback((row: ABMRow): { cv: number; pts: number; source: string } => {
    if (!data) return { cv: 0, pts: 0, source: '' };
    if (row.type === 'coin') {
      const pts = row.risk_points_override ?? 1.0;
      return { cv: row.cash_value_override_zar ?? 0, pts, source: row.risk_points_override != null ? 'override' : 'coin flat' };
    }
    const cv = row.cash_value_override_zar ?? Math.round((row.capacity || 0) * (data.abm_note_value_zar || 200));
    if (row.risk_points_override != null) return { cv, pts: row.risk_points_override, source: 'override' };
    // walk bands ascending, pick the highest whose lower_bound is <= cv
    let pts = 0;
    for (const b of [...data.cash_value_bands].sort((a, b) => a.lower_bound - b.lower_bound)) {
      if (cv >= b.lower_bound) pts = b.points;
    }
    return { cv, pts, source: `≥ R${cv.toLocaleString('en-ZA')}` };
  }, [data]);

  const rescore = React.useCallback(async () => {
    if (!matrixId) return;
    setRescoring(true);
    try {
      const r = await api.post<{ ok: boolean; rescored: number }>(`/risk/matrices/${matrixId}/abm-library/rescore`, {});
      toast.success(`Re-scored ${r.data.rescored} assessment${r.data.rescored === 1 ? '' : 's'}`);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Re-score failed');
    } finally { setRescoring(false); }
  }, [matrixId]);

  const remove = React.useCallback(async (id: string) => {
    if (!matrixId || !window.confirm('Soft-delete this ABM row? Historical assessments keep it, but new imports will stop matching.')) return;
    await api.delete(`/risk/matrices/${matrixId}/abm-library/${id}`);
    toast.success('ABM deactivated'); await load();
  }, [matrixId, load]);

  const restore = React.useCallback(async (id: string) => {
    if (!matrixId) return;
    await api.post(`/risk/matrices/${matrixId}/abm-library/${id}/restore`);
    toast.success('ABM restored'); await load();
  }, [matrixId, load]);

  if (!data) return <Card><CardContent className="py-6 text-center text-raptor-navy/60">Loading ABM library…</CardContent></Card>;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 flex-wrap">
        <div>
          <CardTitle className="text-base">ABM Library ({data.library.filter(r => r.active).length} active, {data.library.length} total)</CardTitle>
          <CardDescription>
            Note value assumption: <b>R{data.abm_note_value_zar}</b> per note. Cash value is derived from
            capacity × note value unless a row pins an explicit override. Risk points auto-map through
            the cash-value bands — override only when a special model needs a fixed score.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-raptor-navy/60 inline-flex items-center gap-1">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
          <Button size="sm" variant="outline" onClick={() => void rescore()} disabled={rescoring} data-testid="abm-rescore">
            <RefreshCw size={13} className="mr-1" /> {rescoring ? 'Re-scoring…' : 'Re-score assessments'}
          </Button>
          <Button size="sm" variant="gradient" onClick={() => setCreating(true)} data-testid="abm-add">
            <Plus size={14} className="mr-1" /> Add ABM
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm font-body">
          <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
            <tr>
              <th className="py-2 px-3 text-left">Model (New / Old)</th>
              <th className="py-2 px-3 text-left">Group</th>
              <th className="py-2 px-3 text-left">Type</th>
              <th className="py-2 px-3 text-right">Capacity</th>
              <th className="py-2 px-3 text-right">Purchase (R)</th>
              <th className="py-2 px-3 text-right">Cash @ Risk (R)</th>
              <th className="py-2 px-3 text-right">Points</th>
              <th className="py-2 px-3"> </th>
            </tr>
          </thead>
          <tbody>
            {data.library.map((r) => {
              const p = previewFor(r);
              return (
                <tr key={r.id} className={`border-t border-border/60 ${!r.active ? 'opacity-50' : ''}`} data-testid={`abm-row-${r.id}`}>
                  <td className="py-2 px-3">
                    <div className="font-semibold">{r.new_name}</div>
                    {r.old_name && <div className="text-[10px] text-raptor-navy/50 font-tabular">{r.old_name}</div>}
                  </td>
                  <td className="py-2 px-3 text-xs text-raptor-navy/70">{r.group || '—'}</td>
                  <td className="py-2 px-3">
                    <Badge variant={r.type === 'coin' ? 'muted' : 'blue'}>{r.type}</Badge>
                  </td>
                  <td className="py-2 px-3 text-right font-tabular">{(r.capacity || 0).toLocaleString('en-ZA')}</td>
                  <td className="py-2 px-3 text-right font-tabular text-raptor-navy/70">{r.purchase_price_zar ? `R ${r.purchase_price_zar.toLocaleString('en-ZA')}` : '—'}</td>
                  <td className="py-2 px-3 text-right font-tabular">
                    <span className={r.cash_value_override_zar != null ? 'font-bold' : ''}>R {p.cv.toLocaleString('en-ZA')}</span>
                    {r.cash_value_override_zar != null && <div className="text-[9px] text-raptor-navy/50 uppercase">override</div>}
                  </td>
                  <td className="py-2 px-3 text-right font-tabular">
                    <span className={r.risk_points_override != null ? 'font-bold' : ''}>{p.pts}</span>
                    <div className="text-[9px] text-raptor-navy/50 uppercase">{r.risk_points_override != null ? 'override' : p.source}</div>
                  </td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`abm-edit-${r.id}`}><Pencil size={12} /></Button>
                    {r.active
                      ? <Button size="sm" variant="ghost" onClick={() => void remove(r.id)} data-testid={`abm-deactivate-${r.id}`}><Trash2 size={12} className="text-red-600" /></Button>
                      : <Button size="sm" variant="ghost" onClick={() => void restore(r.id)} data-testid={`abm-restore-${r.id}`}><RefreshCw size={12} /></Button>}
                  </td>
                </tr>
              );
            })}
            {data.library.length === 0 && (
              <tr><td colSpan={8} className="py-8 text-center text-raptor-navy/50">
                No ABM models yet. Click <b>Add ABM</b> to seed the library.
              </td></tr>
            )}
          </tbody>
        </table>
      </CardContent>

      {(editing || creating) && matrixId && (
        <ABMEditorDialog
          matrixId={matrixId}
          row={editing}
          noteValue={data.abm_note_value_zar}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void load(); }}
        />
      )}
    </Card>
  );
};

const ABMEditorDialog: React.FC<{
  matrixId: string; row: ABMRow | null; noteValue: number;
  onClose: () => void; onSaved: () => void;
}> = ({ matrixId, row, noteValue, onClose, onSaved }) => {
  const isEdit = !!row;
  const [form, setForm] = React.useState<Partial<ABMRow>>(() => row || {
    new_name: '', old_name: '', group: '', type: 'notes', capacity: 0,
    purchase_price_zar: null, cash_value_override_zar: null, risk_points_override: null,
    active: true,
  });
  const [saving, setSaving] = React.useState(false);
  const setField = <K extends keyof ABMRow>(k: K, v: ABMRow[K]) => setForm((f) => ({ ...f, [k]: v }));

  const derivedCV = form.type === 'coin' ? 0 : Math.round(Number(form.capacity || 0) * noteValue);

  const save = async () => {
    if (!form.new_name || !String(form.new_name).trim()) { toast.error('New name is required.'); return; }
    setSaving(true);
    try {
      const body = {
        new_name: form.new_name,
        old_name: form.old_name || '',
        group: form.group || '',
        type: form.type || 'notes',
        capacity: Number(form.capacity || 0),
        purchase_price_zar: form.purchase_price_zar === null || form.purchase_price_zar === undefined ? null : Number(form.purchase_price_zar),
        cash_value_override_zar: form.cash_value_override_zar === null || form.cash_value_override_zar === undefined ? null : Number(form.cash_value_override_zar),
        risk_points_override: form.risk_points_override === null || form.risk_points_override === undefined ? null : Number(form.risk_points_override),
        active: form.active !== false,
      };
      if (isEdit && row) {
        await api.patch(`/risk/matrices/${matrixId}/abm-library/${row.id}`, body);
        toast.success('ABM row updated');
      } else {
        await api.post(`/risk/matrices/${matrixId}/abm-library`, body);
        toast.success('ABM row created');
      }
      onSaved();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-raptor-navy/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="abm-editor-dialog">
      <Card className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle>{isEdit ? `Edit ABM — ${row?.new_name}` : 'Add ABM to library'}</CardTitle>
          <CardDescription>
            Points auto-derive from the cash bands unless you pin an override. New models added here
            flow straight into the scoring engine — click <b>Re-score</b> on the library page to
            update existing assessments and Analytics.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>New ABM name *</Label>
              <Input value={form.new_name || ''} onChange={(e) => setField('new_name', e.target.value)} data-testid="abm-form-new-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Old / legacy name</Label>
              <Input value={form.old_name || ''} onChange={(e) => setField('old_name', e.target.value)} data-testid="abm-form-old-name" />
            </div>
            <div className="space-y-1.5">
              <Label>ABM group</Label>
              <Input value={form.group || ''} onChange={(e) => setField('group', e.target.value)} placeholder="e.g. Protector 3 000" data-testid="abm-form-group" />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type || 'notes'} onChange={(e) => setField('type', e.target.value as 'notes' | 'coin')} data-testid="abm-form-type">
                <option value="notes">Notes</option>
                <option value="coin">Coin</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Capacity ({form.type === 'coin' ? 'kg' : 'notes'})</Label>
              <Input type="number" min={0} value={form.capacity ?? 0} onChange={(e) => setField('capacity', Number(e.target.value || 0))} data-testid="abm-form-capacity" />
            </div>
            <div className="space-y-1.5">
              <Label>Purchase price (R)</Label>
              <Input type="number" min={0} value={form.purchase_price_zar ?? ''} onChange={(e) => setField('purchase_price_zar', e.target.value === '' ? null : Number(e.target.value))} data-testid="abm-form-price" />
            </div>
            <div className="space-y-1.5">
              <Label>Cash value at risk override (R)</Label>
              <Input type="number" min={0} value={form.cash_value_override_zar ?? ''}
                     placeholder={form.type === 'notes' ? `Derived: R ${derivedCV.toLocaleString('en-ZA')}` : 'Optional for coin'}
                     onChange={(e) => setField('cash_value_override_zar', e.target.value === '' ? null : Number(e.target.value))}
                     data-testid="abm-form-cv-override" />
            </div>
            <div className="space-y-1.5">
              <Label>Risk points override</Label>
              <Input type="number" step={0.1} min={0} value={form.risk_points_override ?? ''}
                     placeholder={form.type === 'coin' ? 'Coin flat = 1.0' : 'Auto from cash bands'}
                     onChange={(e) => setField('risk_points_override', e.target.value === '' ? null : Number(e.target.value))}
                     data-testid="abm-form-pts-override" />
            </div>
          </div>
          <div className="p-3 bg-raptor-neutral/30 rounded-md text-[11px] text-raptor-navy/70">
            <b>Preview:</b> capacity {(form.capacity || 0).toLocaleString('en-ZA')} × R{noteValue} ={' '}
            <b>R {derivedCV.toLocaleString('en-ZA')}</b> cash value at risk (unless override is set).
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="abm-form-cancel">Cancel</Button>
            <Button variant="gradient" onClick={() => void save()} disabled={saving} data-testid="abm-form-save">
              <Save size={14} className="mr-1" /> {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create ABM')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

